import { Box, BoxProps, forwardRef, Portal } from '@chakra-ui/react';
import {
  autoUpdate,
  ElementProps,
  FloatingContext,
  FloatingFocusManager,
  FloatingOverlay,
  offset,
  Placement,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { useStableCallback } from '@main/shared/browser';
import {
  Children,
  cloneElement,
  createContext,
  HTMLProps,
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  ReactElement,
  ReactNode,
  Ref,
  useContext,
  useDeferredValue,
  useMemo,
  useState,
} from 'react';

export function EditablePopover({
  children,
  onClose,
  onOpenChange,
  openEvent,
  defaultIsEditing = false,
  placement,
  closeOnBlur = true,
}: {
  children: ReactNode;
  onClose?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  openEvent?: 'click' | 'mousedown';
  defaultIsEditing?: boolean;
  placement?: Placement;
  closeOnBlur?: boolean;
}) {
  const popover = useEditablePopover(
    onClose,
    onOpenChange,
    openEvent,
    defaultIsEditing,
    placement,
    closeOnBlur,
  );
  return (
    <EditablePopoverContext.Provider value={popover}>{children}</EditablePopoverContext.Provider>
  );
}

export const editablePopoverInternalPortalAttribute = 'data-editable-popover-internal';

const EditablePopoverTrigger = forwardRef<{ children: ReactElement }, 'div'>(
  ({ children }, propRef) => {
    const context = useEditablePopoverContext();
    const child = Children.only(children);
    const childRef = (child as ReactElement & { ref: Ref<unknown> }).ref;
    const ref = useMergeRefs([context.refs.setReference, childRef, propRef]);
    const childProps = child.props as {
      onClick?: (event?: MouseEvent) => void;
      onPointerDown?: (event: PointerEvent) => void;
      onKeyDown?: (event: KeyboardEvent) => void;
    };

    const referenceProps = context.getReferenceProps({
      ...(child.props as object),
      ref,
      onPointerDown(event) {
        childProps.onPointerDown?.(event);

        if (!context.isOpen && context.openEvent === 'mousedown') {
          context.setOpen(true);
        }
      },
      onKeyDown(event) {
        childProps.onKeyDown?.(event);

        if (
          !context.isOpen &&
          context.openEvent === 'mousedown' &&
          !event.defaultPrevented &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          context.setOpen(true);
        }
      },
      /* This stops triggering click event on cell itself when clicking on the overflow edit button */
      onClick(event) {
        childProps.onClick?.(event);
        event.stopPropagation();
      },
    });
    const domOnClick = referenceProps['onClick'] as ((event: MouseEvent) => void) | undefined;

    return cloneElement(child, {
      ...referenceProps,
      /* A ComplyKit press child (the pencil EditTrigger) calls onClick with no
       * event: a press does not bubble and carries no DOM event, and the
       * floating-ui chain reads event fields, so the press only needs the
       * popover opened. A DOM child keeps the full merged chain. */
      onClick(event?: MouseEvent) {
        if (event) {
          domOnClick?.(event);
          return;
        }

        childProps.onClick?.();
        if (!context.isOpen) {
          context.setOpen(true);
        }
      },
    });
  },
);
EditablePopover.Trigger = EditablePopoverTrigger;

function EditablePopoverReference({ children }: { children: ReactElement }) {
  const context = useEditablePopoverContext();
  const child = Children.only(children);
  const childRef = (child as ReactElement & { ref: Ref<unknown> }).ref;
  const ref = useMergeRefs([context.refs.setPositionReference, childRef]);
  return cloneElement(child, { ...(child.props as object), ref });
}
EditablePopover.Reference = EditablePopoverReference;

type EditablePopoverContentProps = Omit<BoxProps, 'children'> & {
  children: ReactNode | ((context: ContextType) => ReactNode);
  minWidth?: number | 'xs' | 'sm' | 'md' | 'lg';
  offset?: number | { x: number; y: number };
};

const EditablePopoverContent = forwardRef<EditablePopoverContentProps, 'div'>(
  ({ children, minWidth, offset, ...props }, propRef) => {
    const context = useEditablePopoverContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    // Defer the isOpen value to ensure blur events are triggered before unmounting the component
    const isOpen = useDeferredValue(context.isOpen);
    if (!isOpen) {
      return null;
    }

    const offsetX = typeof offset === 'number' ? offset : (offset?.x ?? 1);
    const offsetY = typeof offset === 'number' ? offset : (offset?.y ?? 1);

    return (
      <Portal>
        <FloatingOverlay lockScroll>
          <FloatingFocusManager context={context.context} initialFocus={-1} visuallyHiddenDismiss>
            <Box
              ref={ref}
              position={context.strategy}
              top={context.y ? context.y + offsetY : 0}
              left={context.x ? context.x + offsetX : 0}
              width="max-content"
              minWidth={minWidth ?? 'sm'}
              {...context.getFloatingProps(props as HTMLProps<HTMLElement>)}
            >
              {typeof children === 'function' ? children(context) : children}
            </Box>
          </FloatingFocusManager>
        </FloatingOverlay>
      </Portal>
    );
  },
);
EditablePopover.Content = EditablePopoverContent;

function useEditablePopover(
  onClose?: () => void,
  onOpenChange?: (isOpen: boolean) => void,
  openEvent?: 'click' | 'mousedown',
  defaultIsEditing?: boolean,
  placement?: Placement,
  closeOnBlur = true,
) {
  const [isOpen, setIsOpen] = useState(defaultIsEditing ?? false);
  const setOpen = useStableCallback((newIsOpen: boolean) => {
    onOpenChange?.(newIsOpen);

    if (!newIsOpen && onClose) {
      onClose();
    }

    setIsOpen(newIsOpen);
  });

  const floating = useFloating({
    placement: placement ?? 'bottom-start',
    open: isOpen,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
      !placement && offset(({ rects }) => -rects.reference.height),
      shift({
        mainAxis: true,
        crossAxis: true,
        padding: 16,
      }),
    ],
  });

  const { context } = floating;

  const click = useClick(context, {
    enabled: !isOpen && openEvent !== 'mousedown',
    event: openEvent,
  });
  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      return !(
        event.target instanceof Element &&
        event.target.closest(`[${editablePopoverInternalPortalAttribute}]`)
      );
    },
  });
  const dismissOnBlur = useDismissOnBlur(context, closeOnBlur);
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, dismissOnBlur, role]);

  return useMemo(
    () => ({
      isOpen,
      openEvent,
      setOpen,
      ...floating,
      ...interactions,
    }),
    [isOpen, openEvent, setOpen, floating, interactions],
  );
}

function useDismissOnBlur({ onOpenChange }: FloatingContext, enabled: boolean) {
  return useMemo(
    (): ElementProps => ({
      floating: {
        onBlur: () => {
          if (enabled) {
            onOpenChange(false);
          }
        },
      },
    }),
    [enabled, onOpenChange],
  );
}

type ContextType = ReturnType<typeof useEditablePopover>;

const EditablePopoverContext = createContext<ContextType | null>(null);

export function useEditablePopoverContext() {
  const context = useContext(EditablePopoverContext);

  if (!context) {
    throw new Error('EditablePopover components must be wrapped in <EditablePopover />');
  }

  return context;
}
